import { createAsyncThunk } from '@reduxjs/toolkit';
import { extractApiError } from '../../utils/errors';
import {
  changeCourierRequest,
  getAvailableCouriersRequest,
} from '../orderDetails/orderDetailsApi';
import { requestTrackingNumbersRequest } from './attentionApi';

/**
 * The couriers this admin is allowed to hand a shipment to.
 *
 * Reused from the order detail: the endpoint derives the admin from the
 * session, so it needs no arguments and works from any screen.
 */
export const fetchAvailableCouriers = createAsyncThunk(
  'attention/fetchAvailableCouriers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAvailableCouriersRequest();
      return response.data || [];
    } catch (error) {
      console.error('Error fetching available couriers:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los couriers'));
    }
  },
  { condition: (_, { getState }) => !getState().attention.couriersLoaded }
);

/**
 * Tracking numbers for a batch of shipments.
 *
 * The endpoint answers with both lists, so a run where half the shipments fail
 * is a successful call with a partial result — not a rejection. Reporting it
 * as an error would hide the ones that did work.
 */
export const requestTrackingNumbers = createAsyncThunk(
  'attention/requestTrackingNumbers',
  async (deliveryIds, { rejectWithValue }) => {
    try {
      const response = await requestTrackingNumbersRequest(deliveryIds);
      return {
        requested: deliveryIds.length,
        succeeded: response.data?.success || [],
        failed: response.data?.failed || [],
      };
    } catch (error) {
      console.error('Error requesting tracking numbers:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los trackings'));
    }
  }
);

/** The only statuses the backend lets you reassign — `deliveries/views.py`. */
export const COURIER_CHANGE_STATUSES = [
  'pending',
  'waiting_for_assignment',
  'created_on_courier',
  'fulfilled',
];

/**
 * Changes the courier of several shipments.
 *
 * One `PATCH` per shipment, in sequence: there is no batch endpoint for this,
 * and it is the only action here that invents a pattern the product does not
 * already have. Sequential rather than parallel so a burst of fifty does not
 * arrive at the courier's API all at once.
 *
 * Three outcomes, and they are not the same thing:
 *  - **skipped**: the shipment's status is one the backend refuses. Not a
 *    failure — nothing was attempted.
 *  - **dispatched**: it worked, but the courier had no stand-by window, so the
 *    parcel is already on its way and the change cannot be walked back.
 *  - **failed**: the call itself went wrong.
 */
export const changeCourierBulk = createAsyncThunk(
  'attention/changeCourierBulk',
  async ({ rows, courier }, { rejectWithValue }) => {
    try {
      const result = { requested: rows.length, succeeded: [], failed: [], skipped: [], dispatched: [] };

      for (const row of rows) {
        if (!COURIER_CHANGE_STATUSES.includes(row.status)) {
          result.skipped.push(row.delivery_id);
          continue;
        }

        try {
          const response = await changeCourierRequest(row.delivery_id, courier);
          result.succeeded.push(row.delivery_id);
          if (response.data?.status === 'dispatching') {
            result.dispatched.push(row.delivery_id);
          }
        } catch (error) {
          result.failed.push(row.delivery_id);
        }
      }

      return result;
    } catch (error) {
      console.error('Error changing couriers:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo cambiar el courier'));
    }
  }
);
